import { useState, useEffect } from "react";
import { Store, Plus, Edit2, Trash2, MapPin, Phone, Truck, Check, X, Search, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import API from "@/utils/api";

const DAYS = [
    { id: "monday", label: "Lunes" },
    { id: "tuesday", label: "Martes" },
    { id: "wednesday", label: "Miércoles" },
    { id: "thursday", label: "Jueves" },
    { id: "friday", label: "Viernes" },
    { id: "saturday", label: "Sábado" },
    { id: "sunday", label: "Domingo" },
];

const defaultOpeningHours = {
    monday: { active: true, shifts: [{ open: "12:00", close: "16:00" }, { open: "19:00", close: "23:30" }] },
    tuesday: { active: true, shifts: [{ open: "12:00", close: "16:00" }, { open: "19:00", close: "23:30" }] },
    wednesday: { active: true, shifts: [{ open: "12:00", close: "16:00" }, { open: "19:00", close: "23:30" }] },
    thursday: { active: true, shifts: [{ open: "12:00", close: "16:00" }, { open: "19:00", close: "23:30" }] },
    friday: { active: true, shifts: [{ open: "12:00", close: "16:00" }, { open: "19:00", close: "00:00" }] },
    saturday: { active: true, shifts: [{ open: "12:00", close: "16:00" }, { open: "19:00", close: "00:00" }] },
    sunday: { active: true, shifts: [{ open: "12:00", close: "16:00" }, { open: "19:00", close: "23:30" }] },
};

export default function AdminBranches() {
    const [branches, setBranches] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    // Dialog states
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: "",
        address: "",
        phone: "",
        delivery_fee: 0,
        min_order: 0,
        radius_km: 0,
        active: true,
        opening_hours: defaultOpeningHours
    });

    const updateOpeningHours = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            opening_hours: {
                ...prev.opening_hours,
                [day]: {
                    ...prev.opening_hours[day],
                    [field]: value
                }
            }
        }));
    };

    const updateShift = (day, shiftIndex, field, value) => {
        setFormData(prev => {
            const dayData = prev.opening_hours[day] || { active: true, shifts: [] };
            const updatedShifts = [...dayData.shifts];
            updatedShifts[shiftIndex] = {
                ...updatedShifts[shiftIndex],
                [field]: value
            };
            
            return {
                ...prev,
                opening_hours: {
                    ...prev.opening_hours,
                    [day]: {
                        ...dayData,
                        shifts: updatedShifts
                    }
                }
            };
        });
    };

    const addShift = (day) => {
        setFormData(prev => {
            const dayData = prev.opening_hours[day] || { active: true, shifts: [] };
            const newShift = { open: "12:00", close: "16:00" };
            
            return {
                ...prev,
                opening_hours: {
                    ...prev.opening_hours,
                    [day]: {
                        ...dayData,
                        shifts: [...dayData.shifts, newShift]
                    }
                }
            };
        });
    };

    const removeShift = (day, shiftIndex) => {
        setFormData(prev => {
            const dayData = prev.opening_hours[day];
            const updatedShifts = dayData.shifts.filter((_, i) => i !== shiftIndex);
            
            return {
                ...prev,
                opening_hours: {
                    ...prev.opening_hours,
                    [day]: {
                        ...dayData,
                        shifts: updatedShifts
                    }
                }
            };
        });
    };

    const fetchBranches = async () => {
        try {
            // Use Admin Endpoint to include inactive branches
            const response = await axios.get(`${API}/admin/branches`, { withCredentials: true });
            setBranches(response.data);
        } catch (error) {
            console.error("Error fetching branches:", error);
            toast.error("Error al cargar sucursales");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    const filteredBranches = branches.filter(b =>
        b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.address?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const resetForm = () => {
        setFormData({
            name: "",
            address: "",
            phone: "",
            delivery_fee: 0,
            min_order: 0,
            radius_km: 0,
            active: true,
            opening_hours: defaultOpeningHours
        });
        setSelectedBranch(null);
    };

    const handleCreate = async () => {
        if (!formData.name) {
            toast.error("El nombre es obligatorio");
            return;
        }

        console.log("➕ [AdminBranches] Creando sucursal con datos:", formData);

        try {
            const response = await axios.post(`${API}/admin/branches`, formData, { withCredentials: true });
            console.log("✅ [AdminBranches] Sucursal creada:", response.data);
            toast.success("Sucursal creada correctamente");
            setShowCreateDialog(false);
            resetForm();
            fetchBranches();
        } catch (error) {
            console.error("❌ [AdminBranches] Error al crear:", error);
            console.error("❌ [AdminBranches] Detalle:", error.response?.data);
            toast.error(error.response?.data?.detail || "Error al crear sucursal");
        }
    };

    const handleUpdate = async () => {
        if (!selectedBranch) return;

        console.log("📝 [AdminBranches] Guardando sucursal:", selectedBranch.branch_id);
        console.log("📝 [AdminBranches] Datos a enviar:", formData);

        try {
            const response = await axios.put(`${API}/admin/branches/${selectedBranch.branch_id}`, formData, { withCredentials: true });
            console.log("✅ [AdminBranches] Respuesta del servidor:", response.data);
            toast.success("Sucursal actualizada correctamente");
            setShowEditDialog(false);
            resetForm();
            fetchBranches();
        } catch (error) {
            console.error("❌ [AdminBranches] Error al guardar:", error);
            console.error("❌ [AdminBranches] Detalle:", error.response?.data);
            toast.error(error.response?.data?.detail || "Error al actualizar sucursal");
        }
    };

    const handleDelete = async () => {
        if (!selectedBranch) return;

        try {
            // Soft Delete (Deactivate)
            await axios.delete(`${API}/admin/branches/${selectedBranch.branch_id}`, { withCredentials: true });
            toast.success("Sucursal desactivada correctamente");
            setShowDeleteDialog(false);
            setSelectedBranch(null);
            fetchBranches();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al desactivar sucursal");
        }
    };

    const handleToggleActive = async (branch) => {
        try {
            const newStatus = !branch.active;
            await axios.put(`${API}/admin/branches/${branch.branch_id}`, {
                ...branch, // Copy original fields
                active: newStatus
            }, { withCredentials: true });

            toast.success(newStatus ? "Sucursal activada" : "Sucursal desactivada");
            fetchBranches();
        } catch (error) {
            toast.error("Error al cambiar estado");
        }
    };

    const openEditDialog = (branch) => {
        setSelectedBranch(branch);
        setFormData({
            name: branch.name,
            address: branch.address || "",
            phone: branch.phone || "",
            delivery_fee: branch.delivery_fee || 0,
            min_order: branch.min_order || 0,
            radius_km: branch.radius_km || 0,
            active: branch.active,
            opening_hours: branch.opening_hours || defaultOpeningHours
        });
        setShowEditDialog(true);
    };

    return (
        <AdminLayout title="Sucursales">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-white">Gestión de Sucursales</h1>
                        <p className="text-gray-400">
                            {filteredBranches.length} {filteredBranches.length === 1 ? "sucursal" : "sucursales"} registradas
                        </p>
                    </div>
                    <Button
                        onClick={() => { resetForm(); setShowCreateDialog(true); }}
                        className="bg-yellow-500 hover:bg-yellow-400 text-black"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nueva Sucursal
                    </Button>
                </div>

                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Buscar por nombre o dirección..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-gray-900 border-gray-700 text-white"
                    />
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {isLoading ? (
                        [1, 2].map(i => <div key={i} className="h-40 bg-gray-800 rounded-xl animate-pulse" />)
                    ) : filteredBranches.map(branch => (
                        <Card key={branch.branch_id} className={`bg-gray-900 border-gray-800 ${!branch.active ? 'opacity-60' : ''}`}>
                            <CardHeader className="pb-2">
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                                            <Store className="w-5 h-5 text-yellow-500" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg text-white">{branch.name}</CardTitle>
                                            <Badge className={branch.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                                {branch.active ? "Activa" : "Inactiva"}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3 pt-2">
                                <div className="text-sm text-gray-400 space-y-1">
                                    {branch.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {branch.address}</div>}
                                    {branch.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {branch.phone}</div>}
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 bg-gray-950 p-2 rounded-lg">
                                    <div>
                                        <span className="block text-gray-400">Delivery Fee</span>
                                        <span className="text-white font-medium">{branch.delivery_fee.toFixed(2)} €</span>
                                    </div>
                                    <div>
                                        <span className="block text-gray-400">Min Order</span>
                                        <span className="text-white font-medium">{branch.min_order.toFixed(2)} €</span>
                                    </div>
                                    <div>
                                        <span className="block text-gray-400">Radio</span>
                                        <span className="text-white font-medium">{branch.radius_km} km</span>
                                    </div>
                                </div>

                                <div className="flex gap-2 pt-2">
                                    <Button variant="outline" size="sm" className="flex-1 border-gray-700 text-white hover:bg-gray-800" onClick={() => openEditDialog(branch)}>
                                        <Edit2 className="w-4 h-4 mr-1" /> Editar
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className={branch.active ? "border-red-500 text-red-500" : "border-green-500 text-green-500"}
                                        onClick={() => handleToggleActive(branch)}
                                    >
                                        {branch.active ? "Desactivar" : "Activar"}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
                if (!open) { setShowCreateDialog(false); setShowEditDialog(false); }
            }}>
                <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{showCreateDialog ? "Nueva Sucursal" : "Editar Sucursal"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 gap-4 py-4">
                        <div>
                            <label className="text-sm text-gray-400">Nombre *</label>
                            <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="bg-gray-800 border-gray-700 mt-1" />
                        </div>
                        <div>
                            <label className="text-sm text-gray-400">Dirección</label>
                            <Input value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} className="bg-gray-800 border-gray-700 mt-1" />
                        </div>
                        <div>
                            <label className="text-sm text-gray-400">Teléfono</label>
                            <Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="bg-gray-800 border-gray-700 mt-1" />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="text-sm text-gray-400">Envío (€)</label>
                                <Input type="number" step="0.5" value={formData.delivery_fee} onChange={e => setFormData({ ...formData, delivery_fee: parseFloat(e.target.value) || 0 })} className="bg-gray-800 border-gray-700 mt-1" />
                            </div>
                            <div>
                                <label className="text-sm text-gray-400">Mínimo (€)</label>
                                <Input type="number" step="1" value={formData.min_order} onChange={e => setFormData({ ...formData, min_order: parseFloat(e.target.value) || 0 })} className="bg-gray-800 border-gray-700 mt-1" />
                            </div>
                            <div>
                                <label className="text-sm text-gray-400">Radio (km)</label>
                                <Input type="number" step="0.5" value={formData.radius_km} onChange={e => setFormData({ ...formData, radius_km: parseFloat(e.target.value) || 0 })} className="bg-gray-800 border-gray-700 mt-1" />
                            </div>
                        </div>

                        {/* Horarios de Apertura */}
                        <div className="mt-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Clock className="w-5 h-5 text-yellow-500" />
                                <h3 className="text-lg font-semibold text-white">Horarios de Apertura (Turnos)</h3>
                            </div>
                            <div className="space-y-4 bg-gray-950 p-4 rounded-lg max-h-96 overflow-y-auto">
                                {DAYS.map(day => {
                                    const dayData = formData.opening_hours[day.id] || { active: true, shifts: [] };
                                    const shifts = dayData.shifts || [];
                                    
                                    return (
                                        <div key={day.id} className="border border-gray-800 rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm font-medium text-white">{day.label}</span>
                                                    <Switch
                                                        checked={dayData.active}
                                                        onCheckedChange={checked => updateOpeningHours(day.id, "active", checked)}
                                                    />
                                                    <span className="text-xs text-gray-500">
                                                        {dayData.active ? "Abierto" : "Cerrado"}
                                                    </span>
                                                </div>
                                                {dayData.active && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => addShift(day.id)}
                                                        className="text-green-500 hover:text-green-400 hover:bg-green-500/10"
                                                    >
                                                        <Plus className="w-4 h-4 mr-1" />
                                                        Turno
                                                    </Button>
                                                )}
                                            </div>
                                            
                                            {dayData.active && shifts.length === 0 && (
                                                <p className="text-xs text-gray-500 italic">
                                                    Sin turnos. Agrega al menos uno.
                                                </p>
                                            )}
                                            
                                            {dayData.active && shifts.map((shift, shiftIndex) => (
                                                <div key={shiftIndex} className="flex items-center gap-2 mb-2">
                                                    <span className="text-xs text-gray-500 w-14">
                                                        Turno {shiftIndex + 1}
                                                    </span>
                                                    <Input
                                                        type="time"
                                                        value={shift.open || "12:00"}
                                                        onChange={e => updateShift(day.id, shiftIndex, "open", e.target.value)}
                                                        className="bg-gray-800 border-gray-700 text-sm flex-1"
                                                    />
                                                    <span className="text-gray-500">-</span>
                                                    <Input
                                                        type="time"
                                                        value={shift.close || "16:00"}
                                                        onChange={e => updateShift(day.id, shiftIndex, "close", e.target.value)}
                                                        className="bg-gray-800 border-gray-700 text-sm flex-1"
                                                    />
                                                    {shifts.length > 1 && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeShift(day.id, shiftIndex)}
                                                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10 px-2"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                                <p className="text-xs text-gray-500 mt-3">
                                    💡 Ejemplo: Turno 1 (12:00-16:00) comidas, Turno 2 (19:00-23:30) cenas
                                </p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }} className="border-gray-700">Cancelar</Button>
                        <Button onClick={showCreateDialog ? handleCreate : handleUpdate} className="bg-yellow-500 text-black hover:bg-yellow-400">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
